import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ButtonModule, CardModule } from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { TranslatePipe } from 'i18n';
import { Logger } from 'shared';
import { MyCardsService } from '../../services/mycards.service';

/**
 * Dashboard pointer to "My Cards" for members who already hold a card
 * entitlement. Renders nothing at all otherwise — no card, no programme on this
 * board, endpoint unavailable — so it can sit at the very top of a dashboard
 * without leaving an empty box behind.
 *
 * Drop `<app-mycards-dashboard-link></app-mycards-dashboard-link>` anywhere a
 * logged-in user is present. Only a link: the page behind it shows the details.
 */
@Component({
  selector: 'app-mycards-dashboard-link',
  standalone: true,
  imports: [CommonModule, RouterModule, ButtonModule, CardModule, IconModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (entitled()) {
      <!-- Deliberately loud: primary-coloured frame and tint, big icon, big button.
           Colours come from the theme variables, so it stands out on every brand. -->
      <c-card class="mycards-dashboard-link border-primary border-2">
        <c-card-body class="d-flex align-items-center flex-wrap gap-3 py-4">
          <svg cIcon name="cilCreditCard" size="3xl" class="flex-shrink-0 text-primary"></svg>
          <div class="flex-grow-1">
            <h4 class="mb-1 fw-bold">{{ 'mycards.widget_title' | translate }}</h4>
            <p class="fs-6 mb-0">{{ 'mycards.widget_text' | translate }}</p>
          </div>
          <a cButton color="primary" size="lg" class="fw-bold px-4" [routerLink]="['/', lang(), 'mycards']">
            {{ 'mycards.widget_button' | translate }}
          </a>
        </c-card-body>
      </c-card>
    }
  `,
  styles: [`
    .mycards-dashboard-link {
      background: rgba(var(--cui-primary-rgb), 0.12);
    }
  `]
})
export class MyCardsDashboardLinkComponent implements OnInit {
  private service = inject(MyCardsService);
  private logger = new Logger('MyCardsDashboardLinkComponent');

  entitled = signal(false);
  lang = signal<string>('en');

  ngOnInit(): void {
    // Same source the My Cards page itself uses for its links.
    this.lang.set(localStorage.getItem('language') || 'en');
    this.service.summary().subscribe({
      next: s => this.entitled.set(s?.card_partner?.entitled === true),
      // An older backend or a hiccup must not put an error on the dashboard: the
      // widget simply stays invisible.
      error: err => this.logger.debug('My Cards summary unavailable', err)
    });
  }
}
